import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { HelpCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { StandardModal } from '@/components/ui/StandardModal';

export const WelcomeOnboardingModal: React.FC = () => {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!user) return;

        // 1. Verifica se o usuário marcou para nunca mais exibir
        const isPermanentlyDismissed = localStorage.getItem(`personalab_welcome_dismissed_${user.id}`) === 'true';
        if (isPermanentlyDismissed) return;

        // 2. Verifica se o usuário já dispensou nesta sessão atual de navegação
        const isSessionDismissed = sessionStorage.getItem(`personalab_welcome_seen_${user.id}`) === 'true';
        if (isSessionDismissed) return;

        // Exibe o modal na entrada da plataforma
        const timer = setTimeout(() => {
            setIsOpen(true);
        }, 400);

        return () => clearTimeout(timer);
    }, [user]);

    const handleDismissSession = () => {
        if (user) {
            sessionStorage.setItem(`personalab_welcome_seen_${user.id}`, 'true');
        }
        setIsOpen(false);
    };

    const handleDismissPermanent = () => {
        if (user) {
            localStorage.setItem(`personalab_welcome_dismissed_${user.id}`, 'true');
            sessionStorage.setItem(`personalab_welcome_seen_${user.id}`, 'true');
        }
        setIsOpen(false);
    };

    const footerNode = (
        <div className="w-full flex flex-col-reverse sm:flex-row items-center justify-end gap-3">
            <button
                type="button"
                onClick={handleDismissPermanent}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-indigo-200 hover:border-indigo-300 text-indigo-600 hover:bg-indigo-50 font-semibold text-xs transition-all shadow-sm active:scale-[0.98] text-center cursor-pointer"
            >
                Não mostrar novamente
            </button>

            <Button
                type="button"
                onClick={handleDismissSession}
                className="w-full sm:w-auto px-8 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 shadow-md shadow-indigo-200 text-xs cursor-pointer"
            >
                Entendi
            </Button>
        </div>
    );

    return (
        <StandardModal
            isOpen={isOpen}
            onClose={handleDismissSession}
            title="Bem-vindo à PersonaLab"
            subtitle="O seu laboratório inteligente de simulações com personas e IA"
            icon={Sparkles}
            iconColor="text-indigo-600"
            iconBgColor="bg-indigo-100"
            maxWidth="lg"
            footer={footerNode}
        >
            <div className="space-y-4">
                {/* Texto Institucional com Instruções Claras */}
                <div className="bg-slate-50/80 border border-slate-200/80 rounded-2xl p-5 space-y-3.5 text-slate-700 text-sm leading-relaxed">
                    <div className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs mt-0.5">
                            1
                        </span>
                        <p className="pt-0.5">
                            Na seção <strong className="text-slate-900 font-semibold">Dashboard</strong> estão os 4 passos que vão guiar sua experiência na plataforma.
                        </p>
                    </div>

                    <div className="flex items-start gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs mt-0.5">
                            2
                        </span>
                        <p className="pt-0.5 leading-relaxed">
                            Sempre que tiver dúvidas, clique no botão{' '}
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-indigo-50 text-indigo-600 rounded-full font-medium text-xs border border-indigo-200 shadow-sm align-middle mx-1 cursor-default">
                                <HelpCircle size={13} />
                                Ajuda?
                            </span>{' '}
                            para entender a função de cada seção.
                        </p>
                    </div>

                    <p className="text-xs font-semibold text-indigo-600 pl-9 pt-0.5">
                        Pronto, agora é só começar.
                    </p>
                </div>
            </div>
        </StandardModal>
    );
};
